import {
  REFERENCE_TYPES,
  type ReferenceType,
  canAdminister,
  canManageCategories,
  canModerate,
  canModerateIdeas,
} from '@cult/domain';
import type { UserRole } from '@cult/domain';
import { createReferenceHooks } from './hooks';
import type { ReferenceResource } from './types';

// Tab order on the Reference data page: praise tags, idea categories, then the organization lists.
export const REFERENCE_TAB_ORDER: readonly ReferenceType[] = [
  REFERENCE_TYPES.CORE_VALUE,
  REFERENCE_TYPES.FRAMEWORK,
  REFERENCE_TYPES.CATEGORY,
  REFERENCE_TYPES.COUNTRY,
  REFERENCE_TYPES.DEPARTMENT,
  REFERENCE_TYPES.TRACK,
  REFERENCE_TYPES.JOB_GRADE,
];

// Organization lists aren't picked anywhere yet, so their copy talks about pickers in general.
const orgResource = (
  type: ReferenceType,
  copy: Pick<ReferenceResource, 'noun' | 'pageTitle' | 'pageSubtitle' | 'newButtonLabel' | 'namePlaceholder'>,
): ReferenceResource => ({
  ...createReferenceHooks(type),
  canView: canModerate,
  canEdit: canAdminister,
  ...copy,
  emptyTitle: `No ${copy.pageTitle.toLowerCase()} yet`,
  emptyHint: `Create one to add it to the ${copy.pageTitle.toLowerCase()} list.`,
  errorTitle: `Couldn't load ${copy.pageTitle.toLowerCase()}`,
  archiveHint: 'Hidden from pickers. You can restore it later.',
  restoreHint: 'Will appear in pickers again.',
});

export const REFERENCE_RESOURCES: Record<ReferenceType, ReferenceResource> = {
  [REFERENCE_TYPES.CATEGORY]: {
    ...createReferenceHooks(REFERENCE_TYPES.CATEGORY),
    canView: canModerateIdeas,
    canEdit: canManageCategories,
    noun: 'category',
    pageTitle: 'Categories',
    pageSubtitle: 'The labels members pick when submitting an idea. Archive instead of delete.',
    newButtonLabel: 'New category',
    emptyTitle: 'No categories yet',
    emptyHint: 'Create one to give members a label to choose from when submitting ideas.',
    errorTitle: "Couldn't load categories",
    archiveHint: 'Hidden from the submit form. You can restore it later.',
    restoreHint: 'Will appear in the submit form again.',
    namePlaceholder: 'People & culture',
  },
  [REFERENCE_TYPES.CORE_VALUE]: {
    ...createReferenceHooks(REFERENCE_TYPES.CORE_VALUE),
    canView: canModerate,
    canEdit: canAdminister,
    noun: 'core value',
    pageTitle: 'Core values',
    pageSubtitle: 'The values members tag when praising a colleague. Archive instead of delete.',
    newButtonLabel: 'New core value',
    emptyTitle: 'No core values yet',
    emptyHint: 'Create one to give members a value to tag when sending praise.',
    errorTitle: "Couldn't load core values",
    archiveHint: 'Hidden from the praise form. You can restore it later.',
    restoreHint: 'Will appear in the praise form again.',
    namePlaceholder: 'Ownership',
  },
  [REFERENCE_TYPES.FRAMEWORK]: {
    ...createReferenceHooks(REFERENCE_TYPES.FRAMEWORK),
    canView: canModerate,
    canEdit: canAdminister,
    noun: 'framework',
    pageTitle: 'Frameworks',
    pageSubtitle: 'The frameworks members tag when praising a colleague. Archive instead of delete.',
    newButtonLabel: 'New framework',
    emptyTitle: 'No frameworks yet',
    emptyHint: 'Create one to give members a framework to tag when sending praise.',
    errorTitle: "Couldn't load frameworks",
    archiveHint: 'Hidden from the praise form. You can restore it later.',
    restoreHint: 'Will appear in the praise form again.',
    namePlaceholder: 'Feedback Framework',
  },
  [REFERENCE_TYPES.COUNTRY]: orgResource(REFERENCE_TYPES.COUNTRY, {
    noun: 'country',
    pageTitle: 'Countries',
    pageSubtitle: 'The countries the organization works in. Archive instead of delete.',
    newButtonLabel: 'New country',
    namePlaceholder: 'Vietnam',
  }),
  [REFERENCE_TYPES.DEPARTMENT]: orgResource(REFERENCE_TYPES.DEPARTMENT, {
    noun: 'department',
    pageTitle: 'Departments',
    pageSubtitle: 'Departments, named by their full path. Archive instead of delete.',
    newButtonLabel: 'New department',
    namePlaceholder: 'KZO / Payments / Settlement',
  }),
  [REFERENCE_TYPES.TRACK]: orgResource(REFERENCE_TYPES.TRACK, {
    noun: 'track',
    pageTitle: 'Tracks',
    pageSubtitle: 'Career tracks. Archive instead of delete.',
    newButtonLabel: 'New track',
    namePlaceholder: 'Operations',
  }),
  [REFERENCE_TYPES.JOB_GRADE]: orgResource(REFERENCE_TYPES.JOB_GRADE, {
    noun: 'job grade',
    pageTitle: 'Job grades',
    pageSubtitle: 'Job grades, junior first. Archive instead of delete.',
    newButtonLabel: 'New job grade',
    namePlaceholder: 'G3',
  }),
};

/** The tabs the role may see, in page order. */
export const visibleReferenceTypes = (role: UserRole): ReferenceType[] =>
  REFERENCE_TAB_ORDER.filter((type) => REFERENCE_RESOURCES[type].canView(role));
